import React from 'react';
import { useSelector } from 'react-redux';
import { AlertBox, H2 } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { TemplatePriceSummary } from './TemplatePriceSummary';
import { pricingSelectors } from '../../state/pricing';
import { currencyLocaleFormatter } from '../../util/formatter';
import useSiteData from '../../hooks/useSiteData';
import { OrderPriceSummary } from './OrderPriceSummary';
import usePricingSummary from '../../hooks/usePricingSummary';
import { cartSelectors } from '../../state/cart';

export default function CartPriceSummary() {
  const [cartMessages, templateBillingMessages] = useTranslations([
    'cartMessages',
    'templateBillingMessages',
  ]);
  const { uiLocalization } = useSiteData();

  const {
    aggregatedOrderPrice,
    shippingPrice,
    productPrice,
    shippingPricingDisplayType,
    shippingIsValid,
    shippingFees,
  } = usePricingSummary();

  const currencyLocale = currencyLocaleFormatter(
    uiLocalization && uiLocalization.locale
  );

  const siteLocale = uiLocalization && uiLocalization.locale;

  const isLoaded = useSelector(state =>
    pricingSelectors.selectPricingIsValid(state.pricing)
  );
  const fractionDigits = useSelector(state =>
    pricingSelectors.selectFractionDigits(state.pricing)
  );
  const cartCurrency = useSelector(state =>
    pricingSelectors.selectCurrency(state.pricing)
  );
  const templateBillingDisplayTypes = useSelector(state =>
    pricingSelectors.selectTemplateBillingDisplayTypes(state.pricing)
  );
  const shippingMethodDisplayValue = useSelector(state =>
    pricingSelectors.selectShippingMethodDisplayValue(state.pricing, siteLocale)
  );
  const readyToEnterCheckout = useSelector(state =>
    cartSelectors.selectReadyToEnterCheckout(state.cart)
  );
  const discountSources = useSelector(state =>
    pricingSelectors.selectDiscountSources(state.pricing)
  );
  const itemPricing = useSelector(state =>
    pricingSelectors.selectItemPricing(state.pricing)
  );

  // Determine whether we need to show the primary cart product total summary.
  // Will return true if there are no cart templates which require billing displays or
  // there is a nonzero total on the cart.
  const showPrimaryProductCartSummary =
    templateBillingDisplayTypes.length === 0 ||
    aggregatedOrderPrice.taxAwareDiscountedCents > 0;

  const showThisOrderHeaderForPrimaryCartSummary =
    templateBillingDisplayTypes.length > 0 &&
    aggregatedOrderPrice.taxAwareDiscountedCents > 0;

  const cartRequiresShowingTemplateBillingSummaries =
    templateBillingDisplayTypes.length > 0;

  if (!readyToEnterCheckout) {
    return (
      <AlertBox skin="error">{cartMessages.checkoutDisabledWarning}</AlertBox>
    );
  }

  return (
    <>
      {(templateBillingDisplayTypes.length === 0 ||
        aggregatedOrderPrice.taxAwareDiscountedCents > 0 ||
        aggregatedOrderPrice.excludedTaxes.length > 0) && (
        <>
          <H2 fontWeight="bold" fontSize="large">
            {cartMessages.orderSummary}
          </H2>
          {showThisOrderHeaderForPrimaryCartSummary ? (
            <H2 marginTop="2" fontSize="large">
              {templateBillingMessages.pricingSummaryPayToday}
            </H2>
          ) : null}
          {showPrimaryProductCartSummary ? (
            <OrderPriceSummary
              fractionDigits={fractionDigits}
              currency={cartCurrency}
              currencyLocale={currencyLocale}
              isLoaded={isLoaded}
              siteLocale={siteLocale}
              shippingPricingDisplayType={shippingPricingDisplayType}
              shippingIsValid={shippingIsValid}
              shippingMethodDisplayValue={shippingMethodDisplayValue}
              shippingPrice={shippingPrice}
              productPrice={productPrice}
              shippingFees={shippingFees}
              excludedTaxes={aggregatedOrderPrice.excludedTaxes}
              orderPrice={aggregatedOrderPrice}
              discountSources={discountSources}
              pricingData={itemPricing}
            />
          ) : null}
        </>
      )}
      {cartRequiresShowingTemplateBillingSummaries
        ? templateBillingDisplayTypes.map(templateBillingDisplayType => (
            <TemplatePriceSummary
              templateBillingDisplayType={templateBillingDisplayType}
              key={templateBillingDisplayType}
              currency={cartCurrency}
              // don't show the heading for a template order if it's the only thing being displayed
              hideHeading={
                !showPrimaryProductCartSummary &&
                templateBillingDisplayTypes.length === 1
              }
            />
          ))
        : null}
    </>
  );
}
